import { Field, Item, LayoutServiceData, LinkField, RouteData } from '@sitecore-content-sdk/nextjs';
import { Data } from '.generated/Foundation.CW.model';
import { Pages } from '.generated/Project.CW.model';
import useDictionary from 'lib/hooks/useDictionary';
import { getUserFacingProviderSpecialties } from 'lib/utils/get-user-facing-provider-specialties';
import { LocationItem, SpecialtyItem } from '../../types/provider-items';
import { PageTemplateIds } from 'lib/constants';
import { JSX } from 'react';
import { usePageData, useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';
import { ProcessedProviderReviews } from '../../types/pg-provider-types';

export interface LabelValueItem {
  fields: {
    value: Field<string>;
  };
  name: string;
}
interface PageTypeData {
  fields: {
    Value: Field<string>;
  };
}

type ConditionItem = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    Title: Field<string>;
  };
};
type TratmentItem = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    Title: Field<string>;
  };
};
type schedulingType = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    cta: LinkField;
    ctaLabelText: Field<string>;
    name: Field<string>;
    specialties: SpecialtyItem[];
  };
};

type acceptingNewPatients = {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    name: Field<string>;
    value: Field<string>;
  };
};

type providerType = {
  id: string;
  name: string;
  fields: {
    name: Field<string>;
  };
};

export type Location = {
  id?: string;
  url?: string;
  fields: {
    Title?: { value: string };
    address1?: { value: string };
    address2?: { value: string };
    city?: { value: string };
    state?: { value?: string; name?: string } | string | null;
    zip?: { value: string };
    addressMapURL?: { value?: { href: string } };
    Latitude?: { value: number | null };
    Longitude?: { value: number | null };
    mobileVisiblePhoneNumber?: { value: string };
    PageType?: { id: string };
  };
};
export type updatedLocation = {
  Title?: string;
  Address?: string;
  AddressMapUrl?: string;
  Latitude?: number;
  Longitude?: number;
  City?: string;
  mobileVisiblePhoneNumber?: string | null;
};
type PageTypeItem = Item & PageTypeData;
// Props for MetaData component
export type MetaDataSeoProps = {
  rendering?: { componentName: string };
  params?: { [key: string]: string };
  route?: RouteData;
};

interface LayoutProps {
  layoutData: LayoutServiceData;
}
type AgeGroupItem = Item & Data.PageSpecific.BlogAgeGroup;
type Enum = {
  fields: {
    Value: Field<string>;
  };
};

type populationItem = Item & Enum;

type specialTopicsItem = Item & Enum;
type contentTypeItem = Item & Enum;
type FormatItem = Item & Enum;
type BlogTagsItem = Item & Data.PageSpecific.BlogTag;
type CategoryItem = Item & Data.PageSpecific.BlogCategory;
type AuthorItem = Item & Data.PageSpecific.Author;
type PageRouteData = RouteData &
  Data.PageSpecific.PublicationInfo &
  Data.BaseTemplateSections.ProviderProfile &
  Data.BaseTemplateSections.Location &
  Data.BaseTemplateSections.OfficeHours &
  Data.BaseTemplateSections.Metadata &
  Data.BaseTemplateSections.Blog &
  Pages.ProviderProfile &
  Data.BaseTemplateSections.TeachingSheets &
  Data.BaseTemplateSections.Specialty;

export const MetaData = ({ layoutData }: LayoutProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const routeFields = layoutData?.sitecore?.route as PageRouteData;
  const templateId = layoutData?.sitecore?.route?.templateId;
  const itemId = layoutData?.sitecore?.route?.itemId?.replace(/-/g, '');
  const templateName = layoutData?.sitecore?.route?.templateName;
  const language = layoutData?.sitecore?.context?.language;
  const itemPath = layoutData?.sitecore?.context?.itemPath;
  const PageType = routeFields?.fields?.PageType as PageTypeItem;
  const State = routeFields?.fields?.state?.fields?.name as Field<string>;
  const siteSettings = useSiteSettings()?.fields;
  const defaultLatitude = siteSettings?.defaultLatitude?.value;
  const defaultLongitude = siteSettings?.defaultLongitude?.value;

  const currentUrl = (() => {
    const publicUrl = process.env.PUBLIC_URL || process.env.NEXT_PUBLIC_URL;

    const itemPath = layoutData.sitecore.context.itemPath;

    if (publicUrl && itemPath) {
      return `${publicUrl}${itemPath}`.toLowerCase();
    }

    // JavaScript fallback
    if (typeof window !== 'undefined') {
      return window.location.href.toLowerCase();
    }

    return '';
  })();

  //   const router = useRouter();
  //   const [currentPathname, setCurrentPathname] = useState('');
  //   useEffect(() => {
  //     if (typeof window !== 'undefined') {
  //       setCurrentPathname(router.asPath);
  //     }
  //   }, [router.asPath]);

  const {
    firstName,
    lastName,
    middleName,
    npi,
    photo,
    videoID,
    degreeTitles,
    additionalDegreeTitles,
    epicPhysicianID,
    Title,
    Description,
    gender,
    mobileVisiblePhoneNumber,
    locationID,
    businessListingName,
    website,
    address1,
    address2,
    addressMapURL,
    city,
    locations,
    locationName,
    blogContentType,
    draftNumber,
    population,
    format,
    specialTopics,
    contentType,
    category,
    author,
    state,
    zip,
    schedulingType,
    acceptingNewPatients,
    providerType,
    //requestAnAppointment,
    specialties,
    ageGroup,
    blogTags,
    //phone,
    date,
    heroImage,
    //primaryLocation,
    Latitude,
    Longitude,
    conditions,
    treatments,
    content,
    officeHours,
    bookingLink,
    webSchedulingLink,
    canText,
    isRecommended,
    //teachingSheets
    fileDescription,
    fileKeywords,
    documentType,
    disclaimerMessage,
    lastReviewDate,
    epicDeptID,
  } = routeFields?.fields || {};

  // Ensure locations is always an array
  const safeLocations = Array.isArray(locations) ? locations : locations ? [locations] : [];

  let degreeTitlesdata = '';
  const categorydata = category as CategoryItem;
  const formatdata = format as FormatItem;
  const authordata = author as AuthorItem;
  const { pageData } = usePageData<{ ratingsAndReviews?: ProcessedProviderReviews }>();
  const ratingsAndReviews = pageData?.ratingsAndReviews;
  const stateValue =
    typeof state === 'string'
      ? state
      : (state && 'value' in state && state.value) ||
        (state && 'name' in state && state.name) ||
        '';

  const parts = [
    address1 && 'value' in address1 ? address1.value : '',
    address2 && 'value' in address2 ? address2.value : '',
    city && 'value' in city ? city.value : '',
    stateValue,
    zip && 'value' in zip ? zip.value : '',
  ].filter((part) => part && String(part).trim() !== '');

  const AddressMapUrlGlobal: LinkField = getAddressMapUrl(
    addressMapURL as LinkField,
    parts.join(', ') || ''
  );

  function getAddressMapUrl(location: LinkField, fulladdress: string): LinkField {
    const MapLocationUrl: string =
      location?.value?.href === '' && fulladdress !== ''
        ? `${siteSettings?.mapServiceUrl?.value?.href}${encodeURIComponent(fulladdress)}`
        : '';

    const getDirectionLink = location?.value?.href
      ? {
          value: {
            ...location?.value,
            target: '_blank',
          },
        }
      : {
          value: {
            href: MapLocationUrl,
            title: getDictionaryValue('GetDirections'),
            text: getDictionaryValue('GetDirections'),
            target: '_blank',
          },
        };
    return getDirectionLink;
  }

  function transformLocations(locations: Location[]): string {
    const updatedLocations = locations.map((loc) => {
      const fields = loc.fields || {};

      const stateValue =
        typeof fields.state === 'string' ? fields.state : fields.state?.value || fields.state?.name;

      const MapUrl: LinkField = loc?.fields?.addressMapURL
        ? getAddressMapUrl(loc?.fields?.addressMapURL as LinkField, fields.Title?.value || '')
        : { value: { href: '' } };

      const parts = [
        fields.address1?.value,
        fields.address2?.value,
        fields.city?.value,
        stateValue,
        fields.zip?.value,
      ].filter((part) => part && String(part).trim() !== '');

      return {
        Title: fields.Title?.value || '',
        ID: loc.id?.replace(/-/g, '') || '',
        UrlPath: loc.url || '',
        Address: parts.join(', '),
        AddressMapUrl: MapUrl?.value?.href || '',
        Latitude: fields.Latitude?.value ?? null,
        Longitude: fields.Longitude?.value ?? null,
        PhoneNumber: fields.mobileVisiblePhoneNumber?.value || '',
      };
    });

    return JSON.stringify(updatedLocations, null, 2);
  }
  function gatPrimaryLocation(location: Location) {
    const fields = location?.fields || {};

    const stateValue =
      typeof fields?.state === 'string'
        ? fields?.state
        : fields?.state?.value || fields?.state?.name || '';

    const MapUrl: LinkField = fields?.addressMapURL
      ? getAddressMapUrl(fields?.addressMapURL as LinkField, fields.Title?.value || '')
      : { value: { href: '' } };

    const parts = [
      fields?.address1?.value,
      fields?.address2?.value,
      fields?.city?.value,
      stateValue,
      fields.zip?.value,
    ].filter((part) => part && String(part).trim() !== '');

    return {
      Title: fields?.Title?.value || '',
      Address: parts?.join(', '),
      AddressMapUrl: MapUrl?.value?.href || '',
      Latitude: fields?.Latitude?.value ?? null,
      Longitude: fields?.Longitude?.value ?? null,
      City: fields?.city?.value || '',
      mobileVisiblePhoneNumber: fields?.mobileVisiblePhoneNumber?.value ?? null,
    };
  }

  function getLocationTitlesString(locations: Location[]): string {
    if (!Array.isArray(locations)) return '';

    const titles = locations
      .map((loc) => loc.fields?.Title?.value?.trim())
      .filter((title) => title && title !== '');

    return titles.join(';');
  }

  function getSpecialtyLabelTextList(specialties?: SpecialtyItem[]): string {
    return (specialties || [])
      .map((item) => item.fields.specialtyLabelText?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getConditionyLabelTextList(conditions?: ConditionItem[]): string {
    return (conditions || [])
      .map((item) => item?.fields?.Title?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getTreatmentLabelTextList(treatments?: TratmentItem[]): string {
    return (treatments || [])
      .map((item) => item?.fields?.Title?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getAgeGroup(ageGroup?: AgeGroupItem[]): string {
    return (ageGroup || [])
      .map((item: AgeGroupItem) => item.fields.displayName?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getBlogTags(blogTags?: BlogTagsItem[]): string {
    return (blogTags || [])
      .map((item: BlogTagsItem) => item.fields.displayName?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getPopulations(population?: populationItem[]): string {
    return (population || [])
      .map((item: populationItem) => item.fields?.Value?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getContentType(contentType?: contentTypeItem[]): string {
    return (contentType || [])
      .map((item: contentTypeItem) => item.fields?.Value?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getSpecialTopics(specialTopics?: specialTopicsItem[]): string {
    return (specialTopics || [])
      .map((item: specialTopicsItem) => item.fields?.Value?.value?.trim())
      .filter((value): value is string => !!value)
      .join(';');
  }
  function getOfficeHourTypeNames(officeHours?: Data.BaseTemplateSections.OfficeHours[]): string {
    return (officeHours || [])
      .map((item: Data.BaseTemplateSections.OfficeHours) =>
        item?.fields?.officeHourType?.name?.trim()
      )
      .filter((value): value is string => !!value)
      .join(',');
  }

  function getOfficeHourJsonString(
    officeHours?: Data.BaseTemplateSections.OfficeHours[]
  ): string | undefined {
    const hours = officeHours
      ?.map((item: Data.BaseTemplateSections.OfficeHours) => {
        return {
          open: item.fields?.open?.value,
          close: item.fields?.close?.value,
          dayOfWeek: item.fields?.officeHourType?.name,
        };
      })
      ?.filter((hours) => hours.open && hours.close && hours.dayOfWeek);
    if (!hours) return;
    return JSON.stringify(hours);
  }

  // const Latitudes =
  //   Latitude?.value !== undefined && Latitude?.value !== null && Latitude?.value?.toString() !== ''
  //     ? Latitude?.value?.toString()
  //     : '0';
  // const Longitudes =
  //   Longitude?.value !== undefined &&
  //   Longitude?.value !== null &&
  //   Longitude?.value?.toString() !== ''
  //     ? Longitude?.value?.toString()
  //     : '0';

  const Latitudes = Latitude?.value?.toString()?.trim()
    ? Latitude.value.toString()
    : defaultLatitude?.toString()?.trim()
      ? defaultLatitude.toString()
      : '0';

  const Longitudes = Longitude?.value?.toString()?.trim()
    ? Longitude.value.toString()
    : defaultLongitude?.toString()?.trim()
      ? defaultLongitude.toString()
      : '0';

  const SchedulingType = schedulingType as schedulingType;
  const AcceptingNewPatients = acceptingNewPatients as unknown as acceptingNewPatients;
  const ProviderType = providerType as unknown as providerType;
  //const RequestAnAppointment = requestAnAppointment?.value?.href;
  const Location = transformLocations(safeLocations);
  // Do not use provider-specific filtering logic for other templates
  const filteredSpecialties =
    templateId === PageTemplateIds.providerProfile
      ? getUserFacingProviderSpecialties(
          specialties as SpecialtyItem[],
          locations as LocationItem[]
        )
      : (specialties as SpecialtyItem[]);
  const Specialties = getSpecialtyLabelTextList(filteredSpecialties);
  const Conditions = getConditionyLabelTextList(conditions as ConditionItem[]);
  const Treatments = getTreatmentLabelTextList(treatments as ConditionItem[]);
  const AgeGroupes = getAgeGroup(ageGroup as AgeGroupItem[]);
  const populations = getPopulations(population as populationItem[]);
  const contenttypes = getContentType(contentType as contentTypeItem[]);
  const specialtopics = getSpecialTopics(specialTopics as specialTopicsItem[]);
  const BlogTags = getBlogTags(blogTags as BlogTagsItem[]);
  const LocationTitles = getLocationTitlesString(safeLocations);
  const officeHourTypeNames = getOfficeHourTypeNames(
    officeHours as Data.BaseTemplateSections.OfficeHours[]
  );
  const officeHoursJsonString = getOfficeHourJsonString(officeHours);
  //const primaryLocationData = gatPrimaryLocation(primaryLocation as Location);
  const primaryLocationData =
    safeLocations.length > 0 ? gatPrimaryLocation(safeLocations[0]) : ({} as updatedLocation);
  const totalProviderLocationsCount = safeLocations?.length;
  let pressGaneyUrl: { shouldShowUrl: boolean; url: string };
  if (locationName?.value) {
    pressGaneyUrl = {
      url: currentUrl + (siteSettings?.pgLocationQueryParms?.value || ''),
      shouldShowUrl: true,
    };
  } else if (templateId === PageTemplateIds.providerProfile) {
    pressGaneyUrl = {
      url: currentUrl,
      shouldShowUrl: true,
    };
  } else {
    pressGaneyUrl = { url: '', shouldShowUrl: false };
  }
  // Fail out if routeFields isn't present
  if (!routeFields) return <></>;

  if (degreeTitles) {
    let combinedDegreeTitles = [...degreeTitles];
    if (additionalDegreeTitles) {
      combinedDegreeTitles = [...degreeTitles, ...additionalDegreeTitles];
    }
    combinedDegreeTitles?.map((item: LabelValueItem, index: number) => {
      degreeTitlesdata =
        index === 0
          ? `${item?.fields?.value?.value}`
          : `${degreeTitlesdata};${item?.fields?.value?.value}`;
    });
  }

  return (
    <div id="searchdata">
      {templateId && (
        <input type="hidden" name="hdntemplateId" id="hdntemplateId" value={templateId} />
      )}
      {bookingLink?.value?.href && (
        <input
          type="hidden"
          name="hdnbookinglink"
          id="hdnbookinglink"
          value={bookingLink?.value?.href}
        />
      )}
      {webSchedulingLink?.value?.href && (
        <input
          type="hidden"
          name="hdnwebschedulinglink"
          id="hdnwebschedulinglink"
          value={webSchedulingLink?.value?.href}
        />
      )}
      {content?.value && (
        <input type="hidden" name="hdnContent" id="hdnContent" value={content?.value} />
      )}
      {SchedulingType?.fields?.name?.value && (
        <input
          type="hidden"
          name="hdnschedulingType"
          id="hdnschedulingType"
          value={SchedulingType?.fields?.name?.value}
        />
      )}
      {SchedulingType?.fields?.cta?.value?.href && (
        <input
          type="hidden"
          name="hdnschedulingTypeCta"
          id="hdnschedulingTypeCta"
          value={SchedulingType?.fields?.cta?.value?.href}
        />
      )}
      {AcceptingNewPatients?.fields?.name?.value && (
        <input
          type="hidden"
          name="hdnAcceptingNewPatients"
          id="hdnAcceptingNewPatients"
          value={AcceptingNewPatients?.fields?.value?.value}
        />
      )}
      {ProviderType?.name && (
        <input
          type="hidden"
          name="hdnProviderType"
          id="hdnProviderType"
          value={ProviderType?.name}
        />
      )}
      <input
        type="hidden"
        name="hdnViewDoctorsAtThisPractice"
        id="hdnViewDoctorsAtThisPractice"
        value={AcceptingNewPatients?.fields?.value?.value}
      />
      {/*{RequestAnAppointment && (
        <input
          type="hidden"
          name="hdnRequestAnAppointment"
          id="hdnRequestAnAppointment"
          value={RequestAnAppointment}
        />
      )}*/}
      {Location && <input type="hidden" name="hdnLocation" id="hdnLocation" value={Location} />}
      {LocationTitles && (
        <input
          type="hidden"
          name="hdnLocationTitles"
          id="hdnLocationTitles"
          value={LocationTitles}
        />
      )}
      {itemId && <input type="hidden" name="hdnitemId" id="hdnitemId" value={itemId} />}
      {templateName && (
        <input type="hidden" name="hdntemplateName" id="hdntemplateName" value={templateName} />
      )}
      {language && <input type="hidden" name="hdnlanguage" id="hdnlanguage" value={language} />}
      {itemPath && <input type="hidden" name="hdnitemPath" id="hdnitemPath" value={itemPath} />}
      {Title && <input type="hidden" name="hdntitle" id="hdntitle" value={Title?.value} />}
      {Title?.value && (
        <input
          type="hidden"
          name="hdninitial"
          id="hdninitial"
          value={Title?.value?.charAt(0)?.toUpperCase()}
        />
      )}
      {Description && (
        <input type="hidden" name="hdndescription" id="hdndescription" value={Description?.value} />
      )}
      {draftNumber && (
        <input type="hidden" name="hdndraftnumber" id="hdndraftnumber" value={draftNumber?.value} />
      )}
      {/* Page Type Data */}
      {PageType?.fields?.Value && (
        <input
          type="hidden"
          name="hdnPageType"
          id="hdnPageType"
          value={PageType?.fields?.Value?.value}
        />
      )}
      {/* Provider Page Data Middle initial,Biography?

Languages spoken ?

npi

web scheduling link?  */}
      {firstName?.value && (
        <input type="hidden" name="hdnfirstName" id="hdnfirstName" value={firstName?.value} />
      )}
      {lastName?.value && (
        <input type="hidden" name="hdnlastName" id="hdnlastName" value={lastName?.value} />
      )}
      {/* {middleName?.value && (
        <input type="hidden" name="hdnmiddleName" id="hdnmiddleName" value={middleName?.value} />
      )} */}
      {/* As per Arvind's suggetion removing empty vslue check from provider middleName field */}
      <input
        type="hidden"
        name="hdnmiddleName"
        id="hdnmiddleName"
        value={middleName?.value ? middleName?.value : ''}
      />
      {npi?.value && <input type="hidden" name="hdnnpi" id="hdnnpi" value={npi?.value} />}

      {/* Add API press ganey reviews so that coveo can index them without JS turned on */}
      {ratingsAndReviews && (
        <>
          <input
            type="hidden"
            name="hdnpgtotalratingcount"
            id="hdnpgtotalratingcount"
            value={ratingsAndReviews?.totalRatingCount}
          />
          <input
            type="hidden"
            name="hdnpgtotalcommentcount"
            id="hdnpgtotalcommentcount"
            value={ratingsAndReviews?.totalCommentCount}
          />
          <input
            type="hidden"
            name="hdnpgoverallrating"
            id="hdnpgoverallrating"
            value={ratingsAndReviews?.overallRating.toFixed(2)}
          />
        </>
      )}

      {Specialties && (
        <input type="hidden" name="hdnSpecialties" id="hdnSpecialties" value={Specialties} />
      )}
      {Conditions && (
        <input type="hidden" name="hdnConditions" id="hdnConditions" value={Conditions} />
      )}
      {Treatments && (
        <input type="hidden" name="hdnTreatments" id="hdnTreatments" value={Treatments} />
      )}
      {gender && <input type="hidden" name="hdngender" id="hdngender" value={gender?.value} />}
      {photo?.value?.src && (
        <input type="hidden" name="hdnphoto" id="hdnphoto" value={photo?.value?.src} />
      )}
      {videoID?.value && (
        <input type="hidden" name="hdnvideoID" id="hdnvideoID" value={videoID?.value} />
      )}
      {heroImage?.value?.src && (
        <input type="hidden" name="hdnblogphoto" id="hdnblogphoto" value={heroImage?.value?.src} />
      )}
      {canText?.value && (
        <input type="hidden" name="hdncanText" id="hdncanText" value={canText?.value ? '1' : '0'} />
      )}
      {isRecommended?.value && (
        <input
          type="hidden"
          name="hdnIsRecommended"
          id="hdnIsRecommended"
          value={isRecommended?.value ? '1' : '0'}
        />
      )}

      {degreeTitlesdata && (
        <input type="hidden" name="hdndegreeTitles" id="hdndegreeTitles" value={degreeTitlesdata} />
      )}
      {ProviderType?.name && (
        <input
          type="hidden"
          name="hdnproviderType"
          id="hdnproviderType"
          value={ProviderType.name}
        />
      )}
      {/* {epicPhysicianID?.value && (
        <input
          type="hidden"
          name="hdnepicPhysicianID"
          id="hdnepicPhysicianID"
          value={epicPhysicianID?.value}
        />
      )} */}
      {/* changed this field name to epicid and in coveo field name is cwepicid */}
      {(epicPhysicianID?.value || epicDeptID?.value) && (
        <input
          type="hidden"
          name={'hdnepicID'}
          id={'hdnepicID'}
          value={epicPhysicianID?.value || epicDeptID?.value}
        />
      )}
      {/* Location Page Data
      mobileVisiblePhoneNumber, locationID, website,address1,address2,addressMapURL,city
      (GUID), state,hour,booking link, businessListingName */}
      {mobileVisiblePhoneNumber && (
        <input
          type="hidden"
          name="hdnmobileVisiblePhoneNumber"
          id="hdnmobileVisiblePhoneNumber"
          value={mobileVisiblePhoneNumber?.value}
        />
      )}
      {/* {phone && (
        <input type="hidden" name="hdnPhoneNumber" id="hdnPhoneNumber" value={phone?.value} />
      )} */}
      {primaryLocationData?.mobileVisiblePhoneNumber && (
        <input
          type="hidden"
          name="hdnPhoneNumber"
          id="hdnPhoneNumber"
          value={primaryLocationData?.mobileVisiblePhoneNumber || ''}
        />
      )}
      {locationID && (
        <input type="hidden" name="hdnlocationID" id="hdnlocationID" value={locationID?.value} />
      )}
      {businessListingName && (
        <input
          type="hidden"
          name="hdnbusinessListingName"
          id="hdnbusinessListingName"
          value={businessListingName?.value}
        />
      )}
      {(primaryLocationData?.Latitude !== 0 || Latitudes) && (
        <input
          type="hidden"
          name="hdnlatitude"
          id="hdnlatitude"
          value={
            primaryLocationData?.Latitude !== 0 && primaryLocationData?.Latitude != null
              ? primaryLocationData.Latitude
              : Latitudes
          }
        />
      )}
      {(primaryLocationData?.Longitude !== 0 || Longitudes) && (
        <input
          type="hidden"
          name="hdnlongitude"
          id="hdnlongitude"
          value={
            primaryLocationData?.Longitude !== 0 && primaryLocationData?.Longitude != null
              ? primaryLocationData.Longitude
              : Longitudes
          }
        />
      )}
      {/* total number of provider locations */}
      {officeHourTypeNames && (
        <input
          type="hidden"
          name="hdnOfficeHoursDays"
          id="hdnOfficeHoursDays"
          value={officeHourTypeNames}
        />
      )}
      {officeHoursJsonString && (
        <input
          type="hidden"
          name="hdnOfficeHoursString"
          id="hdnOfficeHoursString"
          value={officeHoursJsonString}
        />
      )}
      {totalProviderLocationsCount > 0 && (
        <input
          type="hidden"
          name="hdnproviderlocationcount"
          id="hdnproviderlocationcount"
          value={totalProviderLocationsCount}
        />
      )}
      {website?.value && (
        <input
          type="hidden"
          name="hdnwebsitelink"
          id="hdnwebsitelink"
          value={website?.value?.href}
        />
      )}
      {website?.value && (
        <input
          type="hidden"
          name="hdnwebsitetext"
          id="hdnwebsitetext"
          value={website?.value?.text}
        />
      )}
      {address1 && (
        <input type="hidden" name="hdnaddress1" id="hdnaddress1" value={address1?.value} />
      )}
      {address2 && (
        <input type="hidden" name="hdnaddress2" id="hdnaddress2" value={address2?.value} />
      )}
      {AddressMapUrlGlobal?.value && (
        <input
          type="hidden"
          name="hdnaddressMapURL"
          id="hdnaddressMapURL"
          value={AddressMapUrlGlobal?.value?.href}
        />
      )}
      {primaryLocationData?.Address && (
        <input
          type="hidden"
          name="hdnprimarylocationaddress"
          id="hdnprimarylocationaddress"
          value={primaryLocationData?.Address}
        />
      )}
      {primaryLocationData?.Title && (
        <input
          type="hidden"
          name="hdnprimarylocationtitle"
          id="hdnprimarylocationtitle"
          value={primaryLocationData?.Title}
        />
      )}
      {primaryLocationData?.AddressMapUrl && (
        <input
          type="hidden"
          name="hdnprimarylocationaddressmapurl"
          id="hdnprimarylocationaddressmapurl"
          value={primaryLocationData?.AddressMapUrl}
        />
      )}
      {primaryLocationData?.City && (
        <input
          type="hidden"
          name="hdnprimarylocationcity"
          id="hdnprimarylocationcity"
          value={primaryLocationData.City}
        />
      )}
      {addressMapURL?.value && (
        <input
          type="hidden"
          name="hdnaddressMapText"
          id="hdnaddressMapText"
          value={addressMapURL?.value?.text}
        />
      )}
      {city && <input type="hidden" name="hdncity" id="hdncity" value={city?.value} />}
      {pressGaneyUrl.shouldShowUrl && (
        <input
          type="hidden"
          name="hdnpressganeyurl"
          id="hdnpressganeyurl"
          value={pressGaneyUrl.url}
        />
      )}
      {/* Location related fields */}
      {locationName?.value && (
        <input
          type="hidden"
          name="hdnlocationname"
          id="hdnlocationname"
          value={locationName?.value}
        />
      )}
      {State?.value && <input type="hidden" name="hdnstate" id="hdnstate" value={State?.value} />}
      {zip?.value && <input type="hidden" name="hdnzip" id="hdnzip" value={zip?.value} />}
      {/* Speciality Page Data
      name,id?, description?,website,phone */}
      {/* Teaching Sheet Data
     name, id?, Language,Title,MetaDescription, population:multilist,format:droplist,specialtopics:multilist,contenttype:multilist*/}
      {population && (
        <input type="hidden" name="hdnpopulation" id="hdnpopulation" value={populations} />
      )}
      {format && (
        <input
          type="hidden"
          name="hdnformat"
          id="hdnformat"
          value={formatdata?.fields?.Value?.value}
        />
      )}
      {specialTopics && (
        <input type="hidden" name="hdnspecialtopics" id="hdnspecialtopics" value={specialtopics} />
      )}
      {contentType && (
        <input type="hidden" name="hdncontenttype" id="hdncontenttype" value={contenttypes} />
      )}
      {fileDescription && (
        <input
          type="hidden"
          name="hdnteachingSheetDescription"
          id="hdnteachingSheetDescription"
          value={fileDescription?.value}
        />
      )}
      {fileKeywords && (
        <input
          type="hidden"
          name="hdnteachingSheetKeywords"
          id="hdnteachingSheetKeywords"
          value={fileKeywords?.value}
        />
      )}
      {documentType && (
        <input
          type="hidden"
          name="hdnteachingSheetDocumentType"
          id="hdnteachingSheetDocumentType"
          value={documentType?.value}
        />
      )}
      {disclaimerMessage && 'value' in disclaimerMessage && (
        <input
          type="hidden"
          name="hdnDisclaimerMessage"
          id="hdnDisclaimerMessage"
          value={String(disclaimerMessage.value)}
        />
      )}
      {lastReviewDate && 'value' in lastReviewDate && (
        <input
          type="hidden"
          name="hdnLastReviewDate"
          id="hdnLastReviewDate"
          value={String(lastReviewDate.value)}
        />
      )}
      {/* Blog Sheet Data
     	blogContentType:droplist, category:droplink(displayNmae,description), ageGroup:multilist  blogTags:multilist  */}
      {date && <input type="hidden" name="hdnblogdate" id="hdnblogdate" value={date?.value} />}
      {blogContentType && (
        <input
          type="hidden"
          name="hdnblogContentType"
          id="hdnblogContentType"
          value={blogContentType?.value}
        />
      )}
      {category && (
        <input
          type="hidden"
          name="hdncategory"
          id="hdncategory"
          value={categorydata?.fields?.displayName?.value}
        />
      )}
      {author && (
        <input
          type="hidden"
          name="hdnauthorname"
          id="hdnauthorname"
          value={authordata?.fields?.authorName?.value}
        />
      )}
      {author && (
        <input
          type="hidden"
          name="hdnauthortitle"
          id="hdnauthortitle"
          value={authordata?.fields?.authorTitle?.value}
        />
      )}
      {AgeGroupes && <input type="hidden" name="hdnageGroup" id="hdnageGroup" value={AgeGroupes} />}
      {blogTags && <input type="hidden" name="hdnblogTags" id="hdnblogTags" value={BlogTags} />}
    </div>
  );
};
